import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, Notice, Switch, useSnackbar } from '@sauda/ui/kit';
import { OWNER_EVENT_KEYS, type OwnerEventKey } from '@sauda/shared';
import { useAuth } from '../auth/AuthContext.js';
import { api } from '../lib/api.js';

interface OwnerEventsView {
  events: Record<OwnerEventKey, boolean>;
}

/** Which messages the agency lets go to flat owners. Each one saves as it is switched. */
export function OwnerMessagesCard({ readOnly }: { readOnly: boolean }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const cache = useQueryClient();
  const snack = useSnackbar();
  const key = ['owner-messages', auth.agencyId];
  const view = useQuery({
    queryKey: key,
    queryFn: () => api<OwnerEventsView>('/agency/owner-messages'),
  });
  const save = useMutation({
    mutationFn: (events: Partial<Record<OwnerEventKey, boolean>>) =>
      api<OwnerEventsView>('/agency/owner-messages', { method: 'PUT', body: { events } }),
    onSuccess: (next) => {
      cache.setQueryData(key, next);
      snack({ text: t('brand.ownerMessagesSaved') });
    },
  });
  return (
    <Card>
      <h2 className="k-t-title-sm">{t('brand.ownerMessages')}</h2>
      <p className="k-t-body k-muted">{t('brand.ownerMessagesHelp')}</p>
      {save.isError ? <Notice tone="error">{t('brand.ownerMessagesError')}</Notice> : null}
      {view.data === undefined
        ? null
        : OWNER_EVENT_KEYS.map((event) => (
            <Switch
              key={event}
              label={t(`brand.ownerEvent.${event}`)}
              checked={view.data.events[event]}
              disabled={readOnly || save.isPending}
              onChange={(e) => save.mutate({ [event]: e.target.checked })}
            />
          ))}
    </Card>
  );
}
